import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import moment, { Moment } from 'moment';

import {
  Group,
  GroupMissingField,
  Invited,
  ProgramEntity,
  ProgramEntry,
  ProgramItem,
  ProgramType,
  SLOT_DURATION_MINUTES,
  SlotDates,
} from './types';

/**
 * Créneau au format "YYYY-MM-DD HH:mm - HH:mm" (fin optionnelle, jour de fin optionnel).
 * Séparateurs acceptés : "-", "–", "—", "→", ">", "to".
 */
const SLOT_REGEX = /^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}:\d{2})(?:\s*(?:-|–|—|→|>|to)\s*(?:(\d{4}-\d{2}-\d{2})[ T])?(\d{1,2}:\d{2}))?$/;

const SLOT_FORMAT = 'YYYY-MM-DD H:mm';

/** Formats acceptés pour une date de programme (jour seul, ou date complète) */
const DATE_FORMATS = ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD', moment.ISO_8601];

/** Formats acceptés pour un horaire seul */
const TIME_FORMATS = ['HH:mm', 'HH:mm:ss'];

/** La valeur porte une heure ("2026-11-17T09:30", "9h30"), et pas seulement un jour */
const TIME_REGEX = /\d{1,2}\s*[:h]\s*\d{2}/;

/** Parse un créneau "2026-11-17 09:30 - 12:00" en dates ISO début/fin */
export function parseSlot(slot?: string, durationInMinutes = SLOT_DURATION_MINUTES): SlotDates {
  if (!slot || typeof slot !== 'string') return {};
  const match = SLOT_REGEX.exec(slot.trim());
  if (!match) return {};
  const [, day, startTime, endDay, endTime] = match;
  const start = moment(`${day} ${startTime}`, SLOT_FORMAT);
  if (!start.isValid()) return {};
  const end = endTime
    ? moment(`${endDay || day} ${endTime}`, SLOT_FORMAT)
    : start.clone().add(durationInMinutes, 'minutes');
  return {
    startDate: start.format(),
    endDate: end.isValid() && end.isAfter(start) ? end.format() : undefined,
  };
}

type SlotOptions = { slotField?: string; slotDuration?: number };

/** Dates du créneau : startDate/endDate s'ils existent, sinon parsing du champ "slot" */
export function getSlotDates(
  group: Record<string, unknown>,
  { slotField = 'slot', slotDuration }: SlotOptions = {},
): SlotDates {
  if (group?.startDate) {
    return { startDate: group.startDate as string, endDate: group.endDate as string | undefined };
  }
  return parseSlot(get(group, slotField) as string | undefined, slotDuration);
}

/** Formate un créneau en "mardi 17 novembre 2026 - 09:30 → 12:00" */
export function formatSlotRange(slot?: string): string {
  if (!slot) return '';
  const { startDate, endDate } = parseSlot(slot);
  if (!startDate) return slot;
  const start = moment(startDate).format('dddd D MMMM YYYY - HH:mm');
  return endDate ? `${start} → ${moment(endDate).format('HH:mm')}` : start;
}

/** Libellé lisible d'un élément de programme (chaîne brute ou objet) */
export function getProgramLabel(item: ProgramItem): string {
  if (typeof item === 'string') return item;
  return item.label || item.name || item.title || item.value || item._id || '';
}

/** Id d'un élément de programme : les champs multiselect ne stockent que des ids */
export function getProgramItemId(item: ProgramItem): string | undefined {
  const id = typeof item === 'string' ? item : item?._id || item?.value;
  return id?.trim() || undefined;
}

/** Normalise une liste d'éléments de programme (ids bruts ou objets) en liste d'ids */
export function toProgramIds(items?: ProgramItem[] | ProgramItem): string[] {
  if (!items) return [];
  return ([] as ProgramItem[])
    .concat(items)
    .map((item) => getProgramItemId(item))
    .filter((id): id is string => !!id);
}

/** Date de programme ("YYYY-MM-DD", "YYYY-MM-DD HH:mm" ou ISO) */
function parseProgramDate(value?: string): Moment | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const date = moment(value.trim(), DATE_FORMATS);
  return date.isValid() ? date : undefined;
}

/** Applique un horaire ("9h30", "09:30:00") à un jour */
function applyTime(day: Moment | undefined, value?: string): Moment | undefined {
  if (!day || typeof value !== 'string' || !TIME_REGEX.test(value)) return undefined;
  const time = moment(value.trim().replace(/\s*h\s*/, ':'), TIME_FORMATS);
  if (!time.isValid()) return undefined;
  return day.clone().hour(time.hour()).minute(time.minute()).second(0);
}

/**
 * Dates ISO d'un élément de programme : le jour (`startDate`) combiné à ses
 * horaires (`startTime`/`endTime`), ou les dates telles quelles quand elles
 * portent déjà l'heure (sessions, workshops).
 * Renvoie `{}` sans horaire connu : il n'y a alors qu'un jour à afficher.
 */
export function getProgramDates(entity: ProgramEntity): SlotDates {
  const { startDate, endDate, startTime, endTime } = entity;
  const day = parseProgramDate(startDate);
  const start = applyTime(day, startTime) || (TIME_REGEX.test(startDate || '') ? day : undefined);
  if (!start) return {};
  const endDay = parseProgramDate(endDate) || day;
  const end = applyTime(endDay, endTime) || (TIME_REGEX.test(endDate || '') ? endDay : undefined);
  return {
    startDate: start.format(),
    endDate: end?.isAfter(start) ? end.format() : undefined,
  };
}

/** Jour d'un élément de programme, ex. "mardi 17 novembre 2026" (valeur brute si illisible) */
export function formatProgramDay(value?: string): string {
  if (typeof value !== 'string' || !value.trim()) return '';
  return parseProgramDate(value)?.format('dddd D MMMM YYYY') || value.trim();
}

/** Libellé d'une entité de programme : le nom pour une démo, le titre pour une conférence */
export function getProgramEntityLabel(entity: ProgramEntity, type: ProgramType): string {
  const { name, title, label } = entity;
  const values = type === 'conference' ? [title, name, label] : [name, title, label];
  return values.find((value) => value?.trim())?.trim() || '';
}

/**
 * Élément de programme prêt à afficher : le libellé vient de l'entité résolue
 * dans le store (`entitiesById`), et une conférence porte en plus ses dates.
 * Sans entité résolue, on retombe sur le libellé de l'élément, puis sur son id.
 */
export function getProgramEntry(
  item: ProgramItem,
  type: ProgramType,
  entitiesById: Record<string, ProgramEntity> = {},
): ProgramEntry {
  const id = getProgramItemId(item);
  const itemEntity = typeof item === 'object' ? (item as ProgramEntity) : undefined;
  const entity = (id && entitiesById[id]) || itemEntity;
  const label = entity ? getProgramEntityLabel(entity, type) : '';
  const dates: SlotDates = entity && type === 'conference' ? getProgramDates(entity) : {};
  const { startDate, endDate } = dates;
  return {
    id,
    type,
    // Faute d'entité résolue, l'id reste affiché : ça garde l'info exploitable
    label: label || getProgramLabel(item),
    startDate,
    endDate,
    // Une conférence sans horaire n'affiche que son jour
    day: !startDate && type === 'conference' ? formatProgramDay(entity?.startDate) : undefined,
  };
}

/** Éléments de programme dans l'ordre chronologique ; ceux sans horaire ferment la liste */
export function sortProgramByDate(entries: ProgramEntry[]): ProgramEntry[] {
  return orderBy(entries, [({ startDate }) => !startDate, 'startDate'], ['asc', 'asc']);
}

/** Champs du groupe qui ne contiennent jamais d'invités (programme, invités déjà en base…) */
const NON_GUEST_FIELDS = ['children', 'conferences', 'demos', 'invitedUsers', 'program'];

const FIRST_NAME_KEYS = ['firstName', 'firstname', 'first_name', 'prenom', 'prénom'];
const LAST_NAME_KEYS = ['lastName', 'lastname', 'last_name', 'nom'];
const EMAIL_KEYS = ['email', 'mail', 'emailAddress'];

function pick(raw: Record<string, unknown>, keys: string[]): string | undefined {
  const key = keys.find((k) => typeof raw[k] === 'string' && (raw[k] as string).trim());
  return key ? (raw[key] as string).trim() : undefined;
}

/**
 * Normalise un invité saisi dans le formulaire du groupe.
 * Accepte un email brut ("jean@x.fr") ou un objet ({ firstName, lastName, email }).
 * Renvoie `undefined` si l'élément ne ressemble pas à un invité.
 */
function normalizeGuest(raw: unknown): Omit<Invited, '_id' | 'collection'> | undefined {
  if (typeof raw === 'string') {
    const value = raw.trim();
    if (!value) return undefined;
    return value.includes('@') ? { email: value } : { lastName: value };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const item = raw as Record<string, unknown>;
  const email = pick(item, EMAIL_KEYS);
  const firstName = pick(item, FIRST_NAME_KEYS);
  const lastName = pick(item, LAST_NAME_KEYS);
  // Un invité a forcément un email ou un nom : ça évite de confondre avec
  // un élément de programme ({ _id, label }) ou n'importe quel autre tableau.
  if (!email && !firstName && !lastName) return undefined;
  const status = typeof item.status === 'string' ? (item.status as string) : undefined;
  return {
    email,
    firstName,
    lastName,
    status,
    pmr: item.pmr === 'yes' ? 'yes' : undefined,
    parking: item.parking === 'yes' ? 'yes' : undefined,
    licensePlate: typeof item.licensePlate === 'string' ? item.licensePlate : undefined,
  };
}

/** Clé de dédoublonnage : l'email s'il existe, sinon prénom + nom */
export function getGuestKey(guest: Pick<Invited, 'email' | 'firstName' | 'lastName'>): string {
  if (guest.email) return guest.email.trim().toLowerCase();
  return `${guest.firstName || ''} ${guest.lastName || ''}`.trim().toLowerCase();
}

/**
 * Invités saisis dans le formulaire du groupe (champ répétable), avant que
 * les fiches `invited` correspondantes n'existent en base.
 *
 * Si `guestsField` n'est pas configuré, on cherche dans tous les champs du
 * groupe le premier tableau dont les éléments ressemblent à des invités.
 */
export function getGroupGuests(group: Group, guestsField?: string): Invited[] {
  const source = (group as unknown) as Record<string, unknown>;
  const fields = guestsField
    ? [guestsField]
    : Object.keys(source).filter((key) => !NON_GUEST_FIELDS.includes(key));

  const field = fields.find((key) => {
    const value = source[key];
    return Array.isArray(value) && value.some((item) => normalizeGuest(item));
  });
  if (!field) return [];

  return (source[field] as unknown[])
    .map((item, index) => {
      const guest = normalizeGuest(item);
      if (!guest) return undefined;
      return {
        ...guest,
        // Pas de fiche en base : id local, stable tant que la saisie ne bouge pas
        _id: `${group._id}--${field}-${index}`,
        collection: '',
        parentId: group._id,
        isDraft: true,
      } as Invited;
    })
    .filter((guest): guest is Invited => !!guest);
}

/**
 * Liste des invités du groupe : ceux enregistrés en base, complétés par ceux
 * saisis dans le formulaire dont la fiche n'existe pas encore.
 */
export function mergeGuests(registered: Invited[], groupGuests: Invited[]): Invited[] {
  const keys = new Set(registered.map(getGuestKey).filter(Boolean));
  const missing = groupGuests.filter((guest) => {
    const key = getGuestKey(guest);
    return !key || !keys.has(key);
  });
  return [...registered, ...missing];
}

/** Éléments manquants sur un groupe, dans l'ordre d'affichage */
export function getMissingGroupFields(group: Group & { invited?: Invited[] }): GroupMissingField[] {
  const missing: GroupMissingField[] = [];
  if (!group.slot) missing.push('slot');
  const program = [...(group.demos || []), ...(group.conferences || [])].filter((item) =>
    getProgramLabel(item),
  );
  if (!program.length) missing.push('program');
  if (!group.invited?.length) missing.push('guests');
  return missing;
}
